import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import moment from 'moment';
import { IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

moment.updateLocale('en', {
  week: {
    dow: 1,
  },
});

const AxlDateTimePicker = (props) => {
  const { name, variant, timestamp = false, value, dateOnly, timeOnly, onChange, maxDate, minDate } = props;
  const [error, setError] = useState();

  const handleDateChange = (date) => {
    if (!date) {
      onChange({ target: { name, value: '' } });
      return;
    }

    // if (dateOnly) {
    //   date.setUTCHours(12);
    //   date.setMinutes(0);
    // }

    let value;
    if (timestamp) {
      value = date.getTime() / 1000;
    } else {
      value = moment(date).toISOString();
    }

    const event = {
      target: { name, value },
    };

    onChange(event);
  };

  const handleClear = () => {
    const event = {
      target: { name, value: undefined },
    };

    onChange(event);
  };

  const errorMessage = useMemo(() => {
    switch (error) {
      case 'maxDate': {
        return 'Date should not be before maximal date';
      }
      case 'minDate': {
        return 'Date should not be after minimal date';
      }
      case 'invalidDate': {
        return 'Invalid Date Format';
      }

      default: {
        return '';
      }
    }
  }, [error]);

  const generatePicker = useMemo(() => {
    const propsOmitted = _.omit({ format: 'YYYY/MM/DD hh:mm a', ...props }, ['timestamp']);
    if (variant) propsOmitted['inputVariant'] = variant;

    // process timestamp
    if (timestamp && value !== '') {
      try {
        propsOmitted['value'] = new Date(value * 1000);
      } catch (error) {
        // console.error(`Invalid timestamp value: ${value}`);
        propsOmitted['value'] = new Date();
      }
    } else if (value === '') {
      propsOmitted['value'] = new Date();
    } else if (value === null) {
      propsOmitted['value'] = null;
    } else {
      propsOmitted['value'] = moment(propsOmitted['value']);
    }

    if (dateOnly) {
      propsOmitted['format'] = 'YYYY/MM/DD';
    } else if (timeOnly) {
      propsOmitted['format'] = 'hh:mm a';
    }

    const textFieldProps = {
      helperText: errorMessage,
      fullWidth: true,
      size: propsOmitted['size'],
    };

    const actionBarProps = {
      actions: ['clear', 'cancel', 'accept'],
    };

    let picker = (
      <DateTimePicker
        {...propsOmitted}
        onChange={handleDateChange}
        onError={(newError) => setError(newError)}
        slotProps={{
          textField: textFieldProps,
          actionBar: actionBarProps,
        }}
      />
    );

    if (dateOnly) {
      picker = (
        <DatePicker
          {...propsOmitted}
          onChange={handleDateChange}
          onError={(newError) => {
            setError(newError);
          }}
          slotProps={{
            textField: textFieldProps,
            actionBar: actionBarProps,
          }}
        />
      );
    } else if (timeOnly) {
      picker = (
        <DateTimePicker
          {...propsOmitted}
          onChange={handleDateChange}
          onError={(newError) => {
            setError(newError);
          }}
          slotProps={{
            textField: textFieldProps,
            actionBar: actionBarProps,
          }}
        />
      );
    }
    return picker;
  }, [dateOnly, timestamp, variant, name, maxDate, minDate, value]);

  useEffect(() => {
    setError('');
  }, [value]);

  return <LocalizationProvider dateAdapter={AdapterMoment}>{generatePicker}</LocalizationProvider>;
};

AxlDateTimePicker.propsType = {
  onChange: PropTypes.required,
  variant: PropTypes.string,
  name: PropTypes.string.required,
  label: PropTypes.string,
  format: PropTypes.string.required,
  timestamp: PropTypes.bool.required,
};

export default AxlDateTimePicker;
